import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  documentId,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { CareTask, RecurrencePattern } from '@/types/models'
import { toDate } from '@/utils/firestore'
import { getPaginatedCollection } from '@/utils/paginatedQuery'

const COLLECTION = 'tasks'

function docToTask(d: { id: string }, data: Record<string, unknown>): CareTask {
  return {
    id: typeof d === 'string' ? d : (d as { id: string }).id,
    title: data.title as string,
    description: data.description as string,
    dueDate: toDate(data.dueDate),
    status: data.status as CareTask['status'],
    churchMemberId: data.churchMemberId as string,
    assignedUserId: (data.assignedUserId as string) || undefined,
    churchId: data.churchId as string,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    careReasonId: data.careReasonId as string | undefined,
    completedAt: toDate(data.completedAt, true),
    interactionType: data.interactionType as CareTask['interactionType'],
    isSensitive: (data.isSensitive as boolean) ?? false,
    recurrenceId: data.recurrenceId as string | undefined,
    recurrence: data.recurrence
      ? ({
          ...(data.recurrence as Record<string, unknown>),
          endDate: toDate(
            (data.recurrence as Record<string, unknown>).endDate,
            true,
          ),
        } as unknown as RecurrencePattern)
      : undefined,
    recurrenceIndex: data.recurrenceIndex as number | undefined,
    wasRecurring: (data.wasRecurring as boolean) ?? false,
    createdFromTemplateId: data.createdFromTemplateId as string | undefined,
    createdFromTemplateVersion: data.createdFromTemplateVersion as
      | number
      | undefined,
    createdFromTemplateType: data.createdFromTemplateType as
      | CareTask['createdFromTemplateType']
      | undefined,
    batchId: data.batchId as string | undefined,
    batchItemIndex: data.batchItemIndex as number | undefined,
    batchMutationId: data.batchMutationId as string | undefined,
    deletedAt: toDate(data.deletedAt, true),
    deletedBy: data.deletedBy as string | undefined,
    deletedByBatchOperationId: data.deletedByBatchOperationId as
      | string
      | undefined,
  }
}

export async function getTasks(churchId: string): Promise<CareTask[]> {
  const q = query(
    collection(db, COLLECTION),
    where('churchId', '==', churchId),
    where('deletedAt', '==', null),
    orderBy('dueDate', 'asc'),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docToTask(d, d.data()))
}

export const TASKS_PAGE_SIZE = 50

export interface TasksPage {
  tasks: CareTask[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
}

export async function getTasksPaginated(
  churchId: string,
  pageParam: QueryDocumentSnapshot<DocumentData> | null,
): Promise<TasksPage> {
  const { items, lastDoc } = await getPaginatedCollection({
    collectionName: COLLECTION,
    churchId,
    pageParam,
    orderField: 'dueDate',
    orderDirection: 'asc',
    pageSize: TASKS_PAGE_SIZE,
    mapper: (d) => docToTask(d, d.data()),
  })
  return { tasks: items, lastDoc }
}

export async function getTask(id: string): Promise<CareTask> {
  const snap = await getDoc(doc(db, COLLECTION, id))
  if (!snap.exists()) throw new Error('Task not found')
  return docToTask(snap, snap.data())
}

export async function getTasksByIds(ids: string[]): Promise<CareTask[]> {
  if (ids.length === 0) return []

  const batches: string[][] = []
  for (let i = 0; i < ids.length; i += 30) {
    batches.push(ids.slice(i, i + 30))
  }

  const results: CareTask[] = []
  for (const batch of batches) {
    const q = query(
      collection(db, COLLECTION),
      where(documentId(), 'in', batch),
    )
    const snapshot = await getDocs(q)
    results.push(...snapshot.docs.map((d) => docToTask(d, d.data())))
  }
  return results
}

export async function addTask(
  data: Omit<CareTask, 'id' | 'createdAt' | 'updatedAt' | 'completedAt'>,
): Promise<string> {
  const taskData: Record<string, unknown> = {
    ...data,
    dueDate: Timestamp.fromDate(data.dueDate),
    status: 'incomplete',
    isSensitive: data.isSensitive ?? false,
    wasRecurring: data.wasRecurring ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  // Firestore rejects undefined values — remove them
  for (const key of Object.keys(taskData)) {
    if (taskData[key] === undefined) {
      delete taskData[key]
    }
  }

  // Convert recurrence endDate to Timestamp
  if (data.recurrence?.endDate) {
    taskData.recurrence = {
      ...data.recurrence,
      endDate: Timestamp.fromDate(data.recurrence.endDate),
    }
  }

  const ref = await addDoc(collection(db, COLLECTION), taskData)
  return ref.id
}

export async function updateTask(
  id: string,
  data: Partial<Omit<CareTask, 'id'>>,
): Promise<void> {
  const update: Record<string, unknown> = {
    ...data,
    updatedAt: serverTimestamp(),
  }

  // Firestore rejects undefined values — remove them
  for (const key of Object.keys(update)) {
    if (update[key] === undefined) {
      delete update[key]
    }
  }

  if (data.dueDate) {
    update.dueDate = Timestamp.fromDate(data.dueDate)
  }

  if (data.status === 'complete' && !data.completedAt) {
    update.completedAt = serverTimestamp()
  }

  // Remove JS Date fields that shouldn't be written as-is
  delete update.createdAt

  // Remove assignedUserId field from Firestore when unassigning
  if ('assignedUserId' in data && !data.assignedUserId) {
    update.assignedUserId = deleteField()
  }

  await updateDoc(doc(db, COLLECTION, id), update)
}

export async function deleteTask(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id))
}

export async function getRecurringMasterTasks(
  churchId: string,
): Promise<CareTask[]> {
  const q = query(
    collection(db, COLLECTION),
    where('churchId', '==', churchId),
    where('recurrence', '!=', null),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docToTask(d, d.data()))
}

export async function getRecurringInstances(
  churchId: string,
): Promise<CareTask[]> {
  const q = query(
    collection(db, COLLECTION),
    where('churchId', '==', churchId),
    where('recurrenceId', '!=', null),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docToTask(d, d.data()))
}

export async function createRecurringInstance(
  masterTask: CareTask,
  dueDate: Date,
  recurrenceIndex: number,
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    title: masterTask.title,
    description: masterTask.description,
    dueDate: Timestamp.fromDate(dueDate),
    status: 'incomplete',
    churchMemberId: masterTask.churchMemberId,
    ...(masterTask.assignedUserId
      ? { assignedUserId: masterTask.assignedUserId }
      : {}),
    churchId: masterTask.churchId,
    careReasonId: masterTask.careReasonId || null,
    interactionType: masterTask.interactionType || null,
    isSensitive: masterTask.isSensitive,
    recurrenceId: masterTask.id,
    recurrenceIndex,
    wasRecurring: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}
